<template>
  <div class="form-container has-text-left">
    <h1 class="is-size-5 has-text-weight-semibold">Create Participant Profile</h1>
    <p>All fields are mandatory.</p>
    <br>
    <div class="columns">
      <div class="column">
        <TextField
          :label="'First Name'"
          :placeholder="'John'"
          :name="'firstname'"
          :initial-value="firstName"
          :message="firstNameMessage"
          :extraClass="firstNameMessage ? firstNameMessage.class : ''"
          @changedValue="saveFirstName"
        />
      </div>
      <div class="column">
        <TextField
          :label="'Last Name'"
          :placeholder="'Snow'"
          :name="'lastname'"
          :initial-value="lastName"
          :message="lastNameMessage"
          :extraClass="lastNameMessage ? lastNameMessage.class : ''"
          @changedValue="saveLastName"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <TextField
          :label="'Email'"
          :type="'email'"
          :placeholder="'example@example.com'"
          :initial-value="email"
          :message="emailMessage"
          :extraClass="emailMessage ? emailMessage.class : ''"
          @changedValue="saveEmail"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <TextField
          :label="'Password'"
          :type="'password'"
          :placeholder="'Type in a password'"
          :initial-value="password"
          :message="passwordMessage"
          :extraClass="passwordMessage ? passwordMessage.class : ''"
          @changedValue="savePassword"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <DateField
          :label="'Date of Birth'"
          :placeholder="'Select date'"
          :initial-value="birthdate"
          :message="birthdateMessage"
          :extraClass="birthdateMessage ? birthdateMessage.class : ''"
          :initial-view="'year'"
          @changedValue="saveDate"
        />
      </div>
    </div>
    <!-- Parental -->
    <div
      v-if="requiresParentalConsent"
      class="parent-data"
    >
      <p><b>Parent/Guardian Details &amp; Consent</b></p>
      <br>
      <div class="columns" >
        <div class="column">
          <TextField
            :label="'First Name'"
            :placeholder="'Summer'"
            :name="'firstname'"
            :initial-value="guardianFirstName"
            :message="guardianFirstNameMessage"
            :extraClass="guardianFirstNameMessage ? guardianFirstNameMessage.class : ''"
            @changedValue="saveGuardianFirstName"
          />
        </div>
        <div class="column">
          <TextField
            :label="'Last Name'"
            :placeholder="'Smith'"
            :name="'lastname'"
            :initial-value="guardianLastName"
            :message="guardianLastNameMessage"
            :extraClass="guardianLastNameMessage ? guardianLastNameMessage.class : ''"
            @changedValue="saveGuardianLastName"
          />
        </div>
      </div>

      <div class="columns">
        <div class="column">
          <TextField
            :label="'Email'"
            :type="'email'"
            :placeholder="'example@example.com'"
            :initial-value="guardianEmail"
            :message="guardianEmailMessage"
            :extraClass="guardianEmailMessage ? guardianEmailMessage.class : ''"
            @changedValue="saveGuardianEmail"
          />
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <Checkbox
            :label="`I acknowledge being the parent / legal guardian of this participant.`"
            :initial-value="consent"
            class="mb-15"
            @changedValue="saveGuardianConsent"
          />
          <Checkbox
            :label="`I am aware that the judges and other participants will be able to watch my child's entry video.`"
            :initial-value="videoConsent"
            @changedValue="saveGuardianVideoConsent"
          />
        </div>
      </div>
    </div>
    <!-- Geo -->
    <div class="columns">
      <div class="column">
        <TextField
          :label="'Address'"
          :placeholder="'Full address'"
          :name="'street-address'"
          :initial-value="streetName"
          :message="streetNameMessage"
          :extraClass="streetNameMessage ? streetNameMessage.class : ''"
          @instantChangedValue="saveStreetName"
        />
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <Autocomplete
          :label="''"
          :name="'country-list'"
          :placeholder="'Country'"
          :data-source="'countries'"
          :initial-value="country"
          :message="countryMessage"
          @changedValue="saveCountry"
        />
      </div>
      <div
        v-if="hasCountry"
        class="column"
      >
        <Autocomplete
          v-if="country.code === 'US' || country.code === 'CA'"
          :label="''"
          :name="'state-list'"
          :placeholder="'Select State'"
          :data-source="'states'"
          :initial-value="state"
          :message="stateMessage"
          :extraClass="stateMessage ? stateMessage.class : ''"
          @changedValue="saveState"
        />
        <TextField
          v-else
          :label="''"
          :type="'text'"
          :placeholder="'State/Region/District'"
          :initial-value="state.name"
          :message="stateMessage"
          :extraClass="stateMessage ? stateMessage.class : ''"
          :should-autocomplete="'disabled'"
          @instantChangedValue="saveState"
        />
      </div>
      <div
        v-else
        class="column"
      >
        <TextField
          :label="'&nbsp;'"
          :type="'text'"
          :placeholder="'State/Region/District'"
          :initial-value="state.name"
          :message="stateMessage"
          :extraClass="stateMessage ? stateMessage.class : ''"
          :should-autocomplete="'disabled'"
          @instantChangedValue="saveState"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <TextField
          :label="''"
          :placeholder="'City'"
          :name="'city'"
          :initial-value="city"
          :message="cityMessage"
          :extraClass="cityMessage ? cityMessage.class : ''"
          @instantChangedValue="saveCity"
        />
      </div>
      <div class="column">
        <TextField
          :label="''"
          :placeholder="'Zip Code'"
          :name="'zipcode'"
          :initial-value="zipCode"
          :message="zipCodeMessage"
          :extraClass="zipCodeMessage ? zipCodeMessage.class : ''"
          @instantChangedValue="saveZipCode"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <Checkbox
          :label="'&nbsp;'"
          :initial-value="terms"
          @changedValue="saveTerms"
        />
        I agree to the
        <a
          href='#'
          @click.prevent="toggleTermsModal"
        >terms and conditions</a>.
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <Checkbox
          :label="'&nbsp;'"
          :initial-value="privacy"
          @changedValue="savePrivacy"
        />
        I accept the
        <a
          href='#'
          @click.prevent="togglePrivacyModal"
        >privacy policy</a>.
      </div>
    </div>

    <div class="level mt is-mobile">
      <div class="level-left">
        <router-link
          :to="{ name:'home' }"
          class="button is-secondary level-item"
        >Back</router-link>
      </div>
      <div class="level-right">
        <a
          href="#"
          :class="{ 'is-loading': submitting }"
          class="button is-warning has-text-weight-bold level-item"
          :disabled="!isValid || submitting"
          @click.prevent="submitForm"
        >Next</a>
      </div>
    </div>
  <TermsModal
    v-if="isOpenTermsModal"
    :is-open="isOpenTermsModal"
    @closeModal="toggleTermsModal"
  />
  <PrivacyModal
    v-if="isOpenPrivacyModal"
    :is-open="isOpenPrivacyModal"
    @closeModal="togglePrivacyModal"
  />
  </div>
</template>

<script>
import TextField from '@/components/inputs/TextField'
import Autocomplete from '@/components/inputs/autocomplete/Autocomplete'
import DateField from '@/components/inputs/DateField'
import Checkbox from '@/components/inputs/Checkbox'
import TermsModal from '@/components/modals/TermsModal'
import PrivacyModal from '@/components/modals/PrivacyModal'

import emailValidator from 'email-validator'
import moment from 'moment'
import inputMessages from '@/configs/inputMessages'

const minimumLegalAge = 18

export default {
  name: 'UserInfo',
  components: {
    TextField,
    Autocomplete,
    DateField,
    Checkbox,
    TermsModal,
    PrivacyModal
  },
  data () {
    return {
      //  Values
      firstName: this.$store.getters['register/firstName'],
      lastName: this.$store.getters['register/lastName'],
      email: this.$store.getters['register/email'],
      password: this.$store.getters['register/password'],
      passwordConfirm: this.$store.getters['register/password'],
      birthdate: this.$store.getters['register/birthdate'],
      //  Address
      country: this.$store.getters['register/country'],
      state: this.$store.getters['register/state'],
      streetName: this.$store.getters['register/streetName'],
      city: this.$store.getters['register/city'],
      zipCode: this.$store.getters['register/zipCode'],
      //  Legal
      terms: this.$store.getters['register/acceptsTerms'],
      privacy: this.$store.getters['register/acceptsPrivacy'],
      //  Guardian data
      requiresParentalConsent: false,
      guardianFirstName: this.$store.getters['register/guardianFirstName'],
      guardianLastName: this.$store.getters['register/guardianLastName'],
      guardianEmail: this.$store.getters['register/guardianEmail'],
      consent: this.$store.getters['register/guardianConsent'],
      videoConsent: this.$store.getters['register/guardianVideoConsent'],
      //  Errors
      firstNameMessage: false,
      lastNameMessage: false,
      emailMessage: false,
      passwordMessage: inputMessages.passwordRequirement,
      birthdateMessage: inputMessages.parentSignup,
      countryMessage: '',
      stateMessage: '',
      streetNameMessage: '',
      cityMessage: '',
      zipCodeMessage: '',
      guardianFirstNameMessage: false,
      guardianLastNameMessage: false,
      guardianEmailMessage: false,
      //  State
      isOpenTermsModal: false,
      isOpenPrivacyModal: false,
      submitting: false
    }
  },
  mounted () {
    this.validateFields()
    if (this.underageUser(this.birthdate)) {
      this.birthdateMessage = inputMessages.minimumAge
      this.requiresParentalConsent = true
    } else {
      this.birthdateMessage = false
      this.requiresParentalConsent = false
    }
  },
  computed: {
    isValid () {
      return this.validateFields()
    },
    hasCountry () {
      return Object.keys(this.country).length > 0
    }
  },
  watch: {
    requiresParentalConsent () {
      if (!this.requiresParentalConsent) {
        this.$store.commit('register/saveGuardianFirstName', '')
        this.$store.commit('register/saveGuardianLastName', '')
        this.$store.commit('register/saveGuardianEmail', '')
        this.$store.commit('register/saveConsent', false)
        this.$store.commit('register/saveVideoConsent', false)
      }
    }
  },
  methods: {
    saveFirstName (value) {
      if (value.trim().length > 0) {
        this.firstName = value.trim()
        this.firstNameMessage = false
        this.$store.commit('register/saveFirstName', this.firstName)
      } else {
        this.firstNameMessage = inputMessages.required
      }
    },
    saveLastName (value) {
      if (value.trim().length > 0) {
        this.lastName = value.trim()
        this.lastNameMessage = false
        this.$store.commit('register/saveLastName', this.lastName)
      } else {
        this.lastNameMessage = inputMessages.required
      }
    },
    saveEmail (value) {
      if (value.trim().length > 0) {
        if (emailValidator.validate(value)) {
          this.emailMessage = false
          this.email = value
          this.$store.commit('register/saveEmail', this.email)
        } else {
          this.emailMessage = inputMessages.emailFormat
        }
      } else {
        this.emailMessage = inputMessages.required
      }
    },
    savePassword (value) {
      value = value.trim()
      if (value.length >= 6) {
        if (this.validPasswordFormat(value)) {
          this.password = value
          this.passwordMessage = inputMessages.passwordRequirement
          this.$store.commit('register/savePassword', this.password)
        } else {
          this.passwordMessage = inputMessages.passwordError
        }
      } else if (value.length > 0 && value.length < 6) {
        this.passwordMessage = inputMessages.passwordError
      } else {
        this.passwordMessage = inputMessages.required
      }
    },
    saveDate (value) {
      this.birthdate = value
      this.$store.commit('register/saveBirthdate', this.birthdate)
      if (this.underageUser(value)) {
        this.birthdateMessage = inputMessages.minimumAge
        this.requiresParentalConsent = true
      } else {
        this.birthdateMessage = false
        this.requiresParentalConsent = false
      }
    },
    saveCountry (value) {
      this.state = {
        name: '',
        code: '',
        country: ''
      }
      this.stateMessage = ''

      if (Object.keys(value).length > 0) {
        this.country = value
        this.state.country = value.code
        this.countryMessage = ''
        this.$store.commit('register/saveCountry', this.country)
      } else {
        this.country = {
          name: '',
          code: ''
        }
        this.countryMessage = inputMessages.countryRequired
      }
    },
    saveState (value) {
      if (typeof value === 'object') {
        if (Object.keys(value).length > 0) {
          this.state = value
          this.stateMessage = ''
          this.$store.commit('register/saveState', this.state)
        } else {
          this.state = {
            name: '',
            code: '',
            country: ''
          }
          this.stateMessage = inputMessages.stateSelectRequired
        }
      } else if (typeof value === 'string') {
        value = value.trim()
        if (value.length > 0) {
          this.state = {
            name: value,
            code: '',
            country: ''
          }
          this.stateMessage = ''
          this.$store.commit('register/saveState', this.state)
        } else {
          this.state = {
            name: value,
            code: '',
            country: ''
          }
          this.stateMessage = inputMessages.stateValueRequired
        }
      }
    },
    saveStreetName (value) {
      value = value.trim()
      if (value.length > 0) {
        this.streetName = value
        this.streetNameMessage = ''
        this.$store.commit('register/saveStreetName', this.streetName)
      } else {
        this.streetName = ''
        this.streetNameMessage = inputMessages.required
      }
    },
    saveCity (value) {
      value = value.trim()
      if (value.length > 0) {
        this.city = value
        this.cityMessage = ''
        this.$store.commit('register/saveCity', this.city)
      } else {
        this.city = ''
        this.cityMessage = inputMessages.required
      }
    },
    saveZipCode (value) {
      value = value.trim()
      if (value.length > 0) {
        this.zipCode = value
        this.zipCodeMessage = ''
        this.$store.commit('register/saveZipCode', this.zipCode)
      } else {
        this.zipCode = ''
        this.zipCodeMessage = inputMessages.required
      }
    },
    saveGuardianFirstName (value) {
      if (value.trim().length > 0) {
        this.guardianFirstName = value.trim()
        this.guardianFirstNameMessage = false
        this.$store.commit('register/saveGuardianFirstName', this.guardianFirstName)
      } else {
        this.guardianFirstNameMessage = inputMessages.required
      }
    },
    saveGuardianLastName (value) {
      if (value.trim().length > 0) {
        this.guardianLastName = value.trim()
        this.guardianLastNameMessage = false
        this.$store.commit('register/saveGuardianLastName', this.guardianLastName)
      } else {
        this.guardianLastNameMessage = inputMessages.required
      }
    },
    saveGuardianEmail (value) {
      if (value.trim().length > 0) {
        if (emailValidator.validate(value)) {
          this.guardianEmailMessage = false
          this.guardianEmail = value
          this.$store.commit('register/saveGuardianEmail', this.guardianEmail)
        } else {
          this.guardianEmailMessage = inputMessages.emailFormat
        }
      } else {
        this.guardianEmailMessage = inputMessages.required
      }
    },
    saveGuardianConsent (value) {
      this.consent = value
      this.$store.commit('register/saveConsent', this.consent)
    },
    saveGuardianVideoConsent (value) {
      this.videoConsent = value
      this.$store.commit('register/saveVideoConsent', this.videoConsent)
    },
    saveTerms (value) {
      this.terms = value
      this.$store.commit('register/saveTerms', this.terms)
    },
    savePrivacy (value) {
      this.privacy = value
      this.$store.commit('register/savePrivacy', this.privacy)
    },
    // Validation
    validPasswordFormat (value) {
      if (value.match(/[A-Z]/) !== null && value.match(/[0-9]/) !== null) {
        return true
      } else {
        return false
      }
    },
    underageUser (value) {
      const diff = moment().diff(moment(value), 'years')
      if (diff < minimumLegalAge) {
        return true
      } else {
        return false
      }
    },
    validateFields () {
      let valid = false
      if (
        this.firstName.length > 0 &&
        this.lastName.length > 0 &&
        this.email.length > 0 &&
        this.validPasswordFormat(this.password) &&
        typeof this.birthdate === 'object' &&
        this.country.name.length > 0 &&
        this.state.name.length > 0 &&
        this.streetName.length > 0 &&
        this.city.length > 0 &&
        this.zipCode.length > 0 &&
        this.terms &&
        this.privacy
      ) {
        if (this.requiresParentalConsent) {
          if (
            this.guardianFirstName.length > 0 &&
            this.guardianLastName.length > 0 &&
            this.guardianEmail.length > 0 &&
            this.consent &&
            this.videoConsent
          ) {
            valid = true
          }
        } else {
          valid = true
        }
      }
      return valid
    },
    // Submitting
    submitForm () {
      if (this.isValid) {
        this.submitting = true

        let data = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          password: this.password,
          birthdate: this.birthdate.toISOString(),
          terms: this.terms,
          privacy: this.privacy,
          country: this.country,
          state: this.state,
          address: {
            street: this.streetName,
            city: this.city,
            zipCode: this.zipCode
          }
        }

        if (this.requiresParentalConsent) {
          data.isUnderage = true
          data.guardianInfo = {
            firstName: this.guardianFirstName,
            lastName: this.guardianLastName,
            email: this.guardianEmail,
            acceptsResponsibility: this.consent,
            acceptsVideo: this.videoConsent
          }
        }

        this.$store
          .dispatch('register/SAVE_PROFILE', data)
          .then((res) => {
            if ('existingEmail' in res.data) {
              this.submitting = false
              this.emailMessage = inputMessages.emailDuplicate
            } else {
              this.submitting = false
              this.emailMessage = false

              //  Restore session ID from existing unfinished user signup
              if ('sessionId' in res.data) {
                this.$store.commit('register/saveSignupSession', res.data.sessionId)
                this.$router.push({ name: 'register-divisions' })
              } else {
                this.$toasted.error('Error saving user data')
              }
            }
          })
          .catch(err => {
            if (err.response.status === 409) {
              this.emailMessage = inputMessages.emailDuplicate
              this.$toasted.error('Please review the errors in the form')
              this.submitting = false
            } else {
              this.$toasted.error('Error saving user data')
            }
            this.submitting = false
          })
      }
    },
    toggleTermsModal () {
      this.isOpenTermsModal = !this.isOpenTermsModal
    },
    togglePrivacyModal () {
      this.isOpenPrivacyModal = !this.isOpenPrivacyModal
    }
  }
}
</script>

<style lang="scss">
  .form-wrapper{
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100vw;
    min-height: 100vh;
    height: 100%;
    z-index: 10;

    @include until($tablet) {
      align-items: flex-start;
      flex-direction: row;
      flex-wrap: wrap;

      .logo-wrapper, .disclaimer{
        display: none;
      }
    }

    .form-container{
      width: 550px;
      max-width: 550px;
      height: auto;
      padding: 1.5em 1em;
      box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
      border-radius: 4px;
      border: 4px solid $yellow;
      background: #FAFAFA;
      margin-bottom: 15px;

      @include until($tablet) {
        box-shadow: none;
        border-radius: 0px;
        margin-bottom: 0;
        border: 4px solid $primary-color;
      }
      .mt{
        margin-top: 15px;
      }
    }
  }
  .parent-data{
    padding: 15px;
    background: $gray-lighter;
    border-radius: 4px;
    margin-bottom: 15px;
  }
</style>
